import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
// Eager imports — landing-critical only. All admin/portal/heavy dashboard
// modules below are lazy-loaded to keep the initial JS bundle small.
import Hero from "@/components/voor/Hero";
import HomeServices from "@/components/voor/HomeServices";
import FleetVariety from "@/components/voor/FleetVariety";
import WhyChoose from "@/components/voor/WhyChoose";
import CorporateConcierge from "@/components/voor/CorporateConcierge";
import Testimonials from "@/components/voor/Testimonials";
import ReadyToBook from "@/components/voor/ReadyToBook";
import Footer from "@/components/voor/Footer";
import ScrollToTop from "@/components/voor/ScrollToTop";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/voor/ProtectedRoute";
const PrivacyPolicy = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({
    default: module.PrivacyPolicy,
  }))
);
const TermsAndConditions = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({
    default: module.TermsAndConditions,
  }))
);
const ReservationPolicy = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({
    default: module.ReservationPolicy,
  }))
);
const CancellationRefundPolicy = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({
    default: module.CancellationRefundPolicy,
  }))
);
const CaliforniaPrivacyNotice = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({
    default: module.CaliforniaPrivacyNotice,
  }))
);
const AccessibilityStatement = lazy(() =>
  import("@/pages/LegalPages").then((module) => ({
    default: module.AccessibilityStatement,
  }))
);
import BackArrow from "@/components/voor/BackArrow";
import ContactModal from "@/components/voor/ContactModal";
import { CompanyProvider } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { systemApi } from "@/lib/api";
import { registerNativePush } from "@/lib/nativePush";

// Lazy chunks — admin dashboard + authenticated portal + Phase 6F portal.
// These pages are only reachable after login (and admin ones behind the
// admin role guard), so their bundles never ship to a first-time visitor.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Maintenance = lazy(() => import("@/pages/Maintenance"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const VehicleList = lazy(() => import("@/pages/VehicleList"));
const Services = lazy(() => import("@/pages/Services"));
const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const LongDistance = lazy(() => import("@/pages/LongDistance"));
const Airports = lazy(() => import("@/pages/Airports"));
const Corporate = lazy(() => import("@/pages/Corporate"));
const ServiceAreas = lazy(() => import("@/pages/ServiceAreas"));
const AccountDeletion = lazy(() => import("@/pages/AccountDeletion"));
const ReservationWizard = lazy(() => import("@/pages/ReservationWizard"));
const CorporatePortal = lazy(() => import("@/pages/CorporatePortal"));
const PortalLayout = lazy(() => import("@/pages/portal/PortalLayout"));
const Dashboard = lazy(() => import("@/pages/portal/Dashboard"));
const Trips = lazy(() => import("@/pages/portal/Trips"));
const TripDetail = lazy(() => import("@/pages/portal/TripDetail"));
const Saved = lazy(() => import("@/pages/portal/Saved"));
const Profile = lazy(() => import("@/pages/portal/Profile"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/Overview"));
const AdminReservations = lazy(() => import("@/pages/admin/Reservations"));
const AdminServices = lazy(() => import("@/pages/admin/Services"));
const AdminCustomers = lazy(() => import("@/pages/admin/Customers"));
const AdminPricing = lazy(() => import("@/pages/admin/Pricing"));
const AdminPayments = lazy(() => import("@/pages/admin/Payments"));
const AdminStaff = lazy(() => import("@/pages/admin/StaffRoles"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminAudit = lazy(() => import("@/pages/admin/Audit"));
const AdminOperations = lazy(() => import("@/pages/admin/Operations"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const DriverControlRoom = lazy(() => import("@/pages/admin/DriverControlRoom"));
const AutomationCenter = lazy(() => import("@/pages/admin/AutomationCenter"));
const SecurityCenter = lazy(() => import("@/pages/admin/SecurityCenter"));
const OpsCenter = lazy(() => import("@/pages/admin/OpsCenter"));
const InfrastructureCenter = lazy(() => import("@/pages/admin/InfrastructureCenter"));
const AnalyticsCenter = lazy(() => import("@/pages/admin/AnalyticsCenter"));
const ComplianceCenter = lazy(() => import("@/pages/admin/ComplianceCenter"));
const AdminVehicles = lazy(() => import("@/pages/admin/Vehicles"));
const AdminMedia = lazy(() => import("@/pages/admin/Media"));
const AdminDispatch = lazy(() => import("@/pages/admin/Dispatch"));
const AdminCalendar = lazy(() => import("@/pages/admin/Calendar"));
const AdminFinancials = lazy(() => import("@/pages/admin/Financials"));
const AdminContent = lazy(() => import("@/pages/admin/Content"));
const AdminAdvanced = lazy(() => import("@/pages/admin/Advanced"));
const AdminCorporate = lazy(() => import("@/pages/admin/Corporate"));

// Tiny fallback shown while a lazy chunk is downloading. Kept intentionally
// unbranded so it never flashes on the landing page (only appears when
// navigating into admin/portal for the first time).
const LazyFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#050505]"
    aria-label="Loading" role="status">
    <div className="h-6 w-6 rounded-full border-2 border-[#d6d40033] border-t-[#d6d400] animate-spin" />
  </div>
);

function Landing() {
  useEffect(() => {
    let active = true;
    let lenis = null;
    let raf = null;

    import("lenis")
      .then(({ default: Lenis }) => {
        if (!active) return;

        lenis = new Lenis({
          duration: 1.1,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
        });

        window.__lenis = lenis;

        const loop = (time) => {
          if (!active || !lenis) return;
          lenis.raf(time);
          raf = requestAnimationFrame(loop);
        };

        raf = requestAnimationFrame(loop);
      })
      .catch(() => {
        // Native scrolling remains available if the optional
        // smooth-scrolling enhancement cannot be loaded.
      });

    return () => {
      active = false;

      if (raf !== null) {
        cancelAnimationFrame(raf);
      }

      if (lenis) {
        lenis.destroy();
      }

      if (window.__lenis === lenis) {
        delete window.__lenis;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-[#f4f4ea] antialiased">
      <a
        href="#booking"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:vb-btn focus:px-5 focus:py-2 focus:bg-[#050505] focus:text-[#d6d400]"
        data-testid="skip-to-content"
      >
        Skip to booking
      </a>
      <Hero />
      <main id="main">
        <HomeServices />
        <FleetVariety />
        <CorporateConcierge />
        <WhyChoose />
        <Testimonials />
        <ReadyToBook />
      </main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    systemApi.status().then(setStatus).catch(() => setStatus({ maintenance_mode: false }));
  }, [location.pathname]);

  useEffect(() => {
    if (user?.role === "customer") registerNativePush("customer").catch(() => {});
  }, [user]);

  // Maintenance gate: block public + customer pages when ON. Admins and the
  // auth/admin routes stay accessible so staff can sign in and toggle it off.
  const path = location.pathname;
  const exempt = path.startsWith("/admin") || path.startsWith("/login") ||
    path.startsWith("/register") || path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") || path.startsWith("/account-deletion");
  const isAdmin = user && user.role === "admin";
  if (status?.maintenance_mode && !exempt && !isAdmin) {
    return <Maintenance eta={status?.maintenance_eta} />;
  }

  return (
    <>
      <ScrollToTop />
      <BackArrow />
      <Suspense fallback={<LazyFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/long-distance" element={<LongDistance />} />
        <Route path="/airports" element={<Airports />} />
        <Route path="/corporate" element={<Corporate />} />
        <Route path="/service-areas" element={<ServiceAreas />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/reservation-policy" element={<ReservationPolicy />} />
        <Route path="/cancellation-refund-policy" element={<CancellationRefundPolicy />} />
        <Route path="/california-privacy" element={<CaliforniaPrivacyNotice />} />
        <Route path="/accessibility" element={<AccessibilityStatement />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />
        <Route path="/fleet/:category" element={<VehicleList />} />
        <Route path="/corporate/portal" element={<CorporatePortal />} />
        <Route path="/reserve" element={<ReservationWizard />} />
        <Route path="/trips" element={<div className="min-h-screen bg-[#050505] text-[#f4f4ea] px-5 py-20"><main className="mx-auto max-w-5xl"><Trips guestMode /></main></div>} />
        <Route path="/trips/:id" element={<div className="min-h-screen bg-[#050505] text-[#f4f4ea] px-5 py-20"><main className="mx-auto max-w-4xl"><TripDetail guestMode /></main></div>} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<ProtectedRoute><PortalLayout /></ProtectedRoute>}>
          <Route index element={<Dashboard />} />
          <Route path="trips" element={<Trips />} />
          <Route path="trips/:id" element={<TripDetail />} />
          <Route path="saved" element={<Saved />} />
          <Route path="profile" element={<Profile />} />
        </Route>
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
          <Route index element={<AdminOverview />} />
          <Route path="reservations" element={<AdminReservations />} />
          <Route path="services" element={<AdminServices />} />
          <Route path="dispatch" element={<AdminDispatch />} />
          <Route path="calendar" element={<AdminCalendar />} />
          <Route path="vehicles" element={<AdminVehicles />} />
          <Route path="chauffeurs" element={<Navigate to="/admin/drivers" replace />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="corporate" element={<AdminCorporate />} />
          <Route path="pricing" element={<AdminPricing />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="financials" element={<AdminFinancials />} />
          <Route path="content" element={<AdminContent />} />
          <Route path="advanced" element={<AdminAdvanced />} />
          <Route path="staff" element={<AdminStaff />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="operations" element={<AdminOperations />} />
          <Route path="reports" element={<AdminReports />} />
          <Route path="drivers" element={<DriverControlRoom />} />
          <Route path="automation" element={<AutomationCenter />} />
          <Route path="security" element={<SecurityCenter />} />
          <Route path="ops" element={<OpsCenter />} />
          <Route path="infrastructure" element={<InfrastructureCenter />} />
          <Route path="analytics-seo" element={<AnalyticsCenter />} />
          <Route path="compliance" element={<ComplianceCenter />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CompanyProvider>
          <AppRouter />
          <ContactModal />
          <Toaster
            position="top-center"
            theme="dark"
            toastOptions={{
              style: {
                background: "#0b0b0b",
                border: "1.5px solid rgba(214,212,0,0.4)",
                color: "#f4f4ea",
              },
            }}
          />
        </CompanyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
